import { Modal } from 'shared/components/ui';
import { useCreateGraduatingYearMutation } from 'shared/graphql';
import { useSchoolConfiguration, useToast } from 'shared/hooks';
import { recordError } from 'shared/utils/record';
import { CreateGraduatingYearFormValues } from 'shared/validationSchemas';

import { useScorecardStats } from '../hooks/useScorecardStats';
import { CreateOrEditGraduatingYearForm } from './CreateOrEditGraduatingYearForm';

type CreateGraduatingYearModalProps = {
  isOpen: boolean;
  onModalClose: () => void;
  onCreate: () => void | Promise<void>;
};

export function CreateGraduatingYearModal({
  isOpen,
  onModalClose,
  onCreate,
}: CreateGraduatingYearModalProps) {
  const { graduatingYears } = useScorecardStats();
  const { showSuccess, showError } = useToast();
  const { data: school } = useSchoolConfiguration();

  const [createGraduatingYear, { loading: creating }] = useCreateGraduatingYearMutation({
    refetchQueries: ['GraduatingYearsMetrics'],
  });

  const onSubmit = async (formData: CreateGraduatingYearFormValues) => {
    try {
      await createGraduatingYear({
        variables: {
          data: {
            // https://github.com/jebelapp/jebel/issues/1684
            school: { connect: { id: school?.id } },
            graduateCount: formData.alumniCount,
            year: Number(formData.classYear),
          },
        },
      });

      showSuccess(`Class of ${formData.classYear} has been created`);
      onCreate();
      onModalClose();
    } catch (err) {
      recordError(err);

      if (err instanceof Error) {
        showError(err.message);
      }
    }
  };

  return (
    <Modal
      dialogProps={{ maxWidth: 'xs', fullWidth: true, open: isOpen, onClose: onModalClose }}
      titleProps={{ title: 'New Class' }}
    >
      <CreateOrEditGraduatingYearForm
        onSubmit={onSubmit}
        onClose={onModalClose}
        existingGraduatingYears={graduatingYears}
        isLoading={creating}
      />
    </Modal>
  );
}
